import { Button } from "@/components/Button/Button";
import { ExpandedSvg } from "@/components/Icon/Icon";
import { Menu } from "@/components/Menu/Menu";
import { useOrg } from "@/hooks/org";
import { useRoles } from "@/hooks/role";
import { clientApi, REFETCH_TIMEOUT, RoleModel, useCreateUsersApiV1UsersPostMutation } from "@/store/clientApi";
import { patchUsers, store } from "@/store/store";
import { ensureError, localizeError } from "@/utils/helpers";
import { Box, FormControl, IconButton, LinearProgress, MenuItem, TextField, Theme, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

interface UserCreateProps {
  onCreate: () => void;
}

const UserCreate: FC<UserCreateProps> = ({ onCreate }) => {
  const { t } = useTranslation("users");
  const { org } = useOrg();
  const [email, setEmail] = useState<string>("");
  const [role, setRole] = useState<RoleModel | undefined>();
  const [emailError, setEmailError] = useState<boolean>(false);
  const [roleWidth, setRoleWidth] = useState<number>(0);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const { lessPrivilegedRoles: roles, error: rolesError, isLoading: rolesIsLoading } = useRoles();
  const disabled = roles.length === 0;

  const [createUsersApi] = useCreateUsersApiV1UsersPostMutation();

  useEffect(() => {
    if (role === undefined && roles?.[0] !== undefined) {
      setRole(roles[0]);
    }
  }, [roles, role]);

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        // Regex for valid email: up to @ matches for valid start (letters, digits, dots or quoted strings)
        // After @: matches for domain part -- either ipv4 address in square brackets or regular domain name with valid top level domain
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleSubmit = () => {
    setEmailError(false);
    const emails = email.split(",").map((email) => email.trim());
    const emailsInvalid = emails.length === 0 || emails.filter((email) => !validateEmail(email)).length !== 0;

    if (emailsInvalid) {
      setEmailError(true);
    }

    if (!emailsInvalid && role !== undefined) {
      createUser();
    }
  };

  const createUser = async () => {
    try {
      onCreate();
      for (const user of email.split(",").map((email) => email.trim())) {
        await createUsersApi({
          createUserRequest: {
            email: user,
            roles: [role?.id ?? ""]
          }
        }).unwrap();
        patchUsers([{ name: user, email: user }]);
      }

      // Reset email address
      setEmail("");
    } catch (err) {
      const error = ensureError(err);
      toast.error(localizeError(t, error));
    }
    setTimeout(() => store.dispatch(clientApi.util.invalidateTags(["users"])), REFETCH_TIMEOUT); // sometimes the auth0 database is delayed in updating data so we need to refetch after a delay to ensure data is up to date
  };

  if (rolesError) {
    return <>{localizeError(t, ensureError(rolesError))}</>;
  } else if (rolesIsLoading) {
    return <LinearProgress />;
  } else if (!roles) {
    return <></>;
  }

  return (
    <>
      <Typography
        sx={{
          marginBottom: "0.6em",
          color: (theme: Theme) => theme.palette.primary.dark
        }}
      >
        {t("message.addUsers", {
          ...(org?.name && { context: "org", org: org.name })
        })}
      </Typography>
      <FormControl
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between"
        }}
      >
        <Box
          component="div"
          sx={{
            display: "flex",
            alignItems: "center",
            alignContent: "center",
            justifyContent: "flex-start",
            marginBottom: "0.7em",
            gap: "0.5em",
            "& input": {
              width: `calc(100% - ${roleWidth}px)`
            },
            fontSize: {
              xs: "11px",
              sm: "1em"
            }
          }}
        >
          <Box component="div" sx={{ position: "relative", width: "100%" }}>
            <TextField
              required
              id="invite-email"
              type="email"
              placeholder={t("label.enterEmails")}
              disabled={disabled}
              value={email}
              error={emailError}
              onChange={(e) => setEmail(e.target.value)}
              size="small"
              fullWidth
              multiline
              sx={{
                fontSize: "inherit",
                "& textarea": {
                  width: `calc(100% - ${roleWidth}px)`,
                  fontSize: "inherit"
                }
              }}
            />
            <Box
              component="div"
              sx={{
                position: "absolute",
                right: "0",
                top: "0",
                height: "100%",
                display: "flex",
                alignContent: "center",
                alignItems: "center",
                cursor: "default"
              }}
              ref={(ref?: HTMLDivElement) => {
                if (ref) {
                  setRoleWidth(ref.clientWidth);
                }
                ref?.addEventListener("resize", () => {
                  setRoleWidth(ref.clientWidth);
                });
              }}
            >
              <Typography
                sx={{
                  fontSize: "inherit"
                }}
              >
                {t(`role.${role?.name}`, {
                  defaultValue: role?.name ?? ""
                })}
              </Typography>
              <IconButton disabled={disabled} sx={{ height: "40px" }} onClick={handleMenuClick}>
                <ExpandedSvg color="inherit" />
              </IconButton>
            </Box>
          </Box>
          <Button
            id="invite-add"
            variant="contained"
            onClick={handleSubmit}
            sx={{
              padding: "7px 16px"
            }}
            disabled={disabled}
          >
            {t("common:button.invite")}
          </Button>
        </Box>
        <Menu
          innerProps={{
            anchorEl: anchorEl,
            open: openMenu,
            onClose: handleMenuClose,
            onClick: handleMenuClose
          }}
          hideArrow
        >
          {roles.map((role) => {
            /*
              role keys
              t('role.admin')
              t('role.owner')
              t('role.user')
              */
            return (
              <MenuItem key={role.id} value={role.id} onClick={() => setRole(role)}>
                {t(`role.${role.name}`, { defaultValue: role.name })}
              </MenuItem>
            );
          })}
        </Menu>
      </FormControl>
    </>
  );
};

export { UserCreate };
